import { z } from 'zod'
import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { Input } from '@/components/ui/input'
import { fError } from '@/utils/format-error'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from '@/routes/hooks/use-router'
import { uploadFile, deleteFile } from '@/lib/firebase'
import { useState, useEffect, useCallback } from 'react'
import { Upload, X, Image, Video, File } from 'lucide-react'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import {
  ChickenGender,
  MediaItem,
  type Product,
  type ProductFormData,
} from './product.types'

// Product category interface
interface ProductCategory {
  id: string
  name: string
  description?: string
  isActive: boolean
}

// File upload interface
interface FileUpload {
  url: string | File // string for existing files, File for new uploads
  fileName: string
  type?: 'image' | 'video'
  size?: number
}

// Product form schema
const productFormSchema = z.object({
  name: z.string().min(1, 'ชื่อสินค้าจำเป็นต้องระบุ'),
  description: z.string().optional(),
  price: z.number().min(0, 'ราคาต้องมากกว่าหรือเท่ากับ 0'),
  discount: z.number().min(0, 'ส่วนลดต้องมากกว่าหรือเท่ากับ 0').optional(),
  categoryId: z.string().min(1, 'หมวดหมู่สินค้าจำเป็นต้องระบุ'),
  stock: z.number().min(0, 'จำนวนสต็อกต้องมากกว่าหรือเท่ากับ 0'),
  isActive: z.boolean(),
  mediaUrls: z
    .array(
      z.object({
        name: z.string(),
        size: z.number(),
        type: z.enum(['image', 'video']),
        url: z.string(),
        sequence: z.number().optional(),
      }),
    )
    .min(1, 'จำเป็นต้องมีรูปภาพอย่างน้อย 1 รูป')
    .refine((mediaUrls) => mediaUrls.some((media) => media.type === 'image'), {
      message: 'จำเป็นต้องมีรูปภาพอย่างน้อย 1 รูป',
    }),
  // Chicken fields (optional)
  chickenCode: z.string().optional(),
  chickenBirthdate: z.date().optional(),
  chickenGender: z.nativeEnum(ChickenGender).optional(),
  isChickenFighting: z.boolean().optional(),
})

type ProductFormValues = z.infer<typeof productFormSchema>

interface ProductFormProps {
  product?: Product // For edit mode
  mode: 'create' | 'edit'
  categories: ProductCategory[]
}

// Product Media Upload Component
function ProductMediaUpload({
  form,
  uploadedFiles,
  onFilesChange,
  onFileDelete,
  columns = 1,
}: {
  form: UseFormReturn<ProductFormValues>
  uploadedFiles: FileUpload[]
  onFilesChange: (files: FileUpload[]) => void
  onFileDelete?: (deletedUrl: string) => void
  columns?: number
}) {
  const [error, setError] = useState<string>('')
  const maxFiles = 5
  const maxSize = 50 // 50MB
  const acceptedTypes = ['image/*', 'video/*']

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `ไฟล์ ${file.name} มีขนาดใหญ่เกิน ${maxSize}MB`
    }

    // Check file type
    const isValidType = acceptedTypes.some((type) => {
      if (type.endsWith('/*')) {
        const baseType = type.replace('/*', '')
        return file.type.startsWith(baseType)
      }
      return file.type === type
    })

    if (!isValidType) {
      return `ไฟล์ ${file.name} ไม่ใช่ประเภทที่รองรับ`
    }

    return null
  }

  const processFiles = useCallback(
    (fileList: FileList) => {
      const newFiles: FileUpload[] = []
      const errors: string[] = []

      Array.from(fileList).forEach((file) => {
        const error = validateFile(file)
        if (error) {
          errors.push(error)
          return
        }

        const fileType = file.type.startsWith('image/') ? 'image' : 'video'

        const fileItem: FileUpload = {
          url: file,
          fileName: file.name,
          type: fileType,
          size: file.size,
        }

        newFiles.push(fileItem)
      })

      if (errors.length > 0) {
        setError(errors.join(', '))
        setTimeout(() => setError(''), 5000)
        return
      }

      if (uploadedFiles.length + newFiles.length > maxFiles) {
        setError(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์`)
        setTimeout(() => setError(''), 5000)
        return
      }

      const updatedFiles = [...uploadedFiles, ...newFiles]
      onFilesChange(updatedFiles)
      setError('')
    },
    [uploadedFiles, maxFiles, acceptedTypes, maxSize, onFilesChange],
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files)
      }
    },
    [processFiles],
  )

  const removeFile = useCallback(
    async (index: number) => {
      const fileToRemove = uploadedFiles[index]

      if (fileToRemove) {
        // If it's an existing file (string URL), delete from Firebase
        if (typeof fileToRemove.url === 'string') {
          try {
            await deleteFile(fileToRemove.url)

            // Notify parent about deleted file
            if (onFileDelete) {
              onFileDelete(fileToRemove.url)
            }

            toast.success('ลบไฟล์สำเร็จ')
          } catch (error) {
            console.error('Error deleting file from Firebase:', error)
            toast.error('เกิดข้อผิดพลาดในการลบไฟล์')
          }
        }
      }

      const updatedFiles = uploadedFiles.filter((_, i) => i !== index)
      onFilesChange(updatedFiles)
    },
    [uploadedFiles, onFilesChange, onFileDelete],
  )

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (type: 'image' | 'video') => {
    switch (type) {
      case 'image':
        return <Image className="h-4 w-4" />
      case 'video':
        return <Video className="h-4 w-4" />
      default:
        return <File className="h-4 w-4" />
    }
  }

  const getFilePreviewUrl = (file: FileUpload): string => {
    if (typeof file.url === 'string') {
      return file.url
    } else {
      return URL.createObjectURL(file.url)
    }
  }

  const getFileType = (file: FileUpload): 'image' | 'video' => {
    if (file.type) return file.type

    if (typeof file.url === 'string') {
      // Try to determine from URL extension
      const extension = file.url.split('.').pop()?.toLowerCase()
      if (['mp4', 'avi', 'mov', 'wmv'].includes(extension || '')) {
        return 'video'
      }
      return 'image'
    } else {
      return file.url.type.startsWith('image/') ? 'image' : 'video'
    }
  }

  const getFileSize = (file: FileUpload): number => {
    if (file.size) return file.size

    if (typeof file.url === 'string') {
      return 0 // Unknown size for existing files
    } else {
      return file.url.size
    }
  }

  return (
    <Card className="border rounded-lg">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-black">
          รูปภาพและวิดีโอสินค้า
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="mediaUrls"
          render={() => (
            <FormItem>
              <FormLabel className="text-base font-medium text-black">
                รูปภาพและวิดีโอสินค้า *
              </FormLabel>
              <FormControl>
                <div className="space-y-4">
                  {/* Upload Area */}
                  <div className="space-y-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        document.getElementById('product-file-input')?.click()
                      }
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      อัปโหลดไฟล์
                    </Button>
                  </div>

                  <input
                    id="product-file-input"
                    type="file"
                    multiple
                    accept={acceptedTypes.join(',')}
                    onChange={handleFileInput}
                    className="hidden"
                  />

                  {/* Error Message */}
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  {/* File List */}
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        ไฟล์ที่เลือก ({uploadedFiles.length}/{maxFiles})
                      </h4>
                      <div
                        className="grid gap-3"
                        style={{
                          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                        }}
                      >
                        {uploadedFiles.map((file, index) => {
                          const fileType = getFileType(file)
                          const fileSize = getFileSize(file)
                          const previewUrl = getFilePreviewUrl(file)

                          return (
                            <div
                              key={index}
                              className="relative group border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors min-w-[190px]"
                            >
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>

                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  {fileType === 'image' ? (
                                    <img
                                      src={previewUrl}
                                      alt={file.fileName}
                                      className="h-12 w-12 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                                      {getFileIcon(fileType)}
                                    </div>
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {file.fileName}
                                  </p>
                                  {fileSize > 0 && (
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(fileSize)}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 capitalize">
                                    {fileType}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}

// Product Information Component
function ProductInformation({
  form,
  categories,
}: {
  form: UseFormReturn<ProductFormValues>
  categories: ProductCategory[]
}) {
  return (
    <Card className="border rounded-lg">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-black">
          ข้อมูลสินค้า
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Product Name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium text-black">
                ชื่อสินค้า *
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="ระบุชื่อสินค้า"
                  className="rounded-lg border"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium text-black">
                คำอธิบายสินค้า
              </FormLabel>
              <FormControl>
                <textarea
                  placeholder="ระบุคำอธิบายสินค้า"
                  className="w-full border rounded-lg p-3 min-h-[100px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Price and Discount Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-medium text-black">
                  ราคา (บาท) *
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="rounded-lg border"
                    {...field}
                    value={
                      field.value === 0 || field.value === undefined
                        ? ''
                        : field.value
                    }
                    onChange={(e) => {
                      const value = e.target.value.replace(/^0+(?!$)/, '')
                      if (value === '') {
                        field.onChange('')
                      } else {
                        field.onChange(Number(value))
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="discount"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-medium text-black">
                  ส่วนลด (บาท)
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="rounded-lg border"
                    {...field}
                    value={
                      field.value === 0 || field.value === undefined
                        ? ''
                        : field.value
                    }
                    onChange={(e) => {
                      const value = e.target.value.replace(/^0+(?!$)/, '')
                      if (value === '') {
                        field.onChange('')
                      } else {
                        field.onChange(Number(value))
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Category and Stock Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium text-black">
                    หมวดหมู่สินค้า *
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="เลือกหมวดหมู่" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <SelectItem value="" disabled>
                          ไม่พบหมวดหมู่
                        </SelectItem>
                      ) : (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <FormField
              control={form.control}
              name="stock"
              render={({ field }) => {
                const isChickenFighting = form.watch('isChickenFighting')

                return (
                  <FormItem>
                    <FormLabel className="text-base font-medium text-black">
                      จำนวนสต็อก *
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        className="rounded-lg border"
                        disabled={isChickenFighting}
                        value={field.value}
                        onChange={(e) => {
                          const value = e.target.value.replace(/^0+(?!$)/, '')
                          if (value === '') {
                            field.onChange('')
                          } else {
                            field.onChange(Number(value))
                          }
                        }}
                      />
                    </FormControl>
                    {isChickenFighting && (
                      <p className="text-sm text-muted-foreground">
                        จำกัดสต็อกของสินค้านี้เป็น 1 ตัว
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
          </div>
        </div>

        {/* Chicken Information */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-base font-semibold text-black">
            ข้อมูลไก่ (ถ้ามี)
          </h3>

          {/* Chicken Code */}
          <FormField
            control={form.control}
            name="chickenCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-medium text-black">
                  รหัสไก่
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="ระบุรหัสไก่"
                    className="rounded-lg border"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Chicken Birthdate */}
          <FormField
            control={form.control}
            name="chickenBirthdate"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-medium text-black">
                  วันเกิดไก่
                </FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    className="rounded-lg border"
                    value={
                      field.value instanceof Date
                        ? field.value.toISOString().split('T')[0]
                        : field.value
                          ? new Date(field.value).toISOString().split('T')[0]
                          : ''
                    }
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? new Date(e.target.value) : undefined,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Chicken Gender and Fighting Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="chickenGender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium text-black">
                    เพศไก่
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="เลือกเพศ" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ChickenGender).map((gender) => (
                        <SelectItem key={gender} value={gender}>
                          {gender === ChickenGender.MALE
                            ? 'ตัวผู้'
                            : gender === ChickenGender.FEMALE
                              ? 'ตัวเมีย'
                              : gender}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isChickenFighting"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-x-3 space-y-0">
                  <FormControl>
                    <Switch
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-base font-medium text-black">
                      ไก่ชน
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      เป็นไก่ชนหรือไม่
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Product Settings Component
function ProductSettings({ form }: { form: UseFormReturn<ProductFormValues> }) {
  return (
    <Card className="border rounded-lg">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-black">
          การตั้งค่า
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status */}
        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 space-x-3 space-y-0">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-base font-medium text-black">
                  สินค้าพร้อมขาย
                </FormLabel>
                <p className="text-sm text-muted-foreground">
                  เปิดการขายสินค้านี้ในระบบ
                </p>
              </div>
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}

export default function ProductForm({
  product,
  mode,
  categories,
}: ProductFormProps) {
  const submitting = useBoolean(false)
  const router = useRouter()

  // States
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([])

  // Form setup
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: product
      ? {
          name: product.name,
          description: product.description,
          price: product.price,
          discount: product.discount,
          categoryId: product.categoryId,
          stock: product.stock,
          isActive: product.isActive,
          mediaUrls: product.mediaUrls,
          // Chicken fields
          chickenCode: product.chickenCode,
          chickenBirthdate: product.chickenBirthdate
            ? product.chickenBirthdate instanceof Date
              ? product.chickenBirthdate
              : new Date(product.chickenBirthdate)
            : undefined,
          chickenGender: product.chickenGender,
          isChickenFighting: product.isChickenFighting,
        }
      : {
          name: '',
          description: '',
          price: 0,
          discount: 0,
          categoryId: '',
          stock: 0,
          isActive: true,
          mediaUrls: [],
          // Chicken fields
          chickenCode: undefined,
          chickenBirthdate: undefined,
          chickenGender: undefined,
          isChickenFighting: undefined,
        },
    mode: 'onChange',
  })

  // เพิ่ม useEffect สำหรับ watch isChickenFighting
  const isChickenFighting = form.watch('isChickenFighting')
  useEffect(() => {
    if (isChickenFighting && form.watch('stock') !== 0) {
      form.setValue('stock', 1, { shouldDirty: true })
    }
  }, [isChickenFighting, form])

  const {
    formState: { isDirty },
  } = form

  // Initialize uploaded files if editing
  useEffect(() => {
    if (product && mode === 'edit') {
      // Convert existing mediaUrls to FileUpload format for display
      const existingFiles: FileUpload[] = product.mediaUrls.map(
        (media, index) => ({
          url: media.url, // Use the URL directly for existing files
          fileName: media.name || `ไฟล์ ${index + 1}`,
          type: media.type,
          size: media.size || 0,
        }),
      )

      setUploadedFiles(existingFiles)

      // Initialize form with existing files without triggering dirty state
      const mediaUrls: MediaItem[] = existingFiles.map((file, index) => ({
        url: typeof file.url === 'string' ? file.url : `placeholder-${index}`,
        type: file.type || 'image',
        sequence: index,
        name: file.fileName,
        size: file.size || 0,
      }))

      form.setValue('mediaUrls', mediaUrls, { shouldDirty: false })
    }
  }, [product, mode, form])

  // Handle file upload changes
  const handleFilesChange = (files: FileUpload[]) => {
    setUploadedFiles(files)

    // Update form's mediaUrls field with placeholder data for validation
    const mediaUrls: MediaItem[] = files.map((file, index) => ({
      url: typeof file.url === 'string' ? file.url : `placeholder-${index}`,
      type: file.type || 'image',
      sequence: index,
      name: file.fileName,
      size: file.size || 0,
    }))

    // Always trigger dirty state when files change
    form.setValue('mediaUrls', mediaUrls, { shouldDirty: true })

    // Debug: Log file changes
  }

  // Handle file deletion from Firebase
  const handleFileDelete = (deletedUrl: string) => {
    // Update the form's mediaUrls to remove the deleted file
    const currentMediaUrls = form.getValues('mediaUrls')
    const updatedMediaUrls = currentMediaUrls.filter(
      (media) => media.url !== deletedUrl,
    )

    // Always trigger dirty state when files are deleted
    form.setValue('mediaUrls', updatedMediaUrls, { shouldDirty: true })
  }

  // Handle form submission
  const onSubmit = async (values: ProductFormValues) => {
    try {
      submitting.onTrue()

      // Ensure chickenBirthdate is a Date object if it exists
      const processedValues = {
        ...values,
        chickenBirthdate: values.chickenBirthdate
          ? values.chickenBirthdate instanceof Date
            ? values.chickenBirthdate
            : new Date(values.chickenBirthdate)
          : undefined,
      }

      let finalMediaUrls = values.mediaUrls

      // Upload files to Firebase Storage
      const processedMediaUrls = await Promise.all(
        uploadedFiles.map(async (fileItem, index) => {
          // If it's an existing file (string URL)
          if (typeof fileItem.url === 'string') {
            return {
              url: fileItem.url,
              type: fileItem.type || 'image',
              sequence: index,
              name: fileItem.fileName,
              size: fileItem.size || 0,
            }
          }

          // Upload new file
          const url = await uploadFile(
            fileItem.url,
            'products',
            fileItem.fileName,
          )
          return {
            url,
            type: fileItem.type || 'image',
            sequence: index,
            name: fileItem.fileName,
            size: fileItem.size || fileItem.url.size,
          }
        }),
      )

      // Sort mediaUrls by type (images first, then videos) and assign sequence
      finalMediaUrls = processedMediaUrls
        .sort((a, b) => {
          // Sort by type: image first, then video
          if (a.type === 'image' && b.type === 'video') return -1
          if (a.type === 'video' && b.type === 'image') return 1
          return 0
        })
        .map((media, index) => ({
          ...media,
          sequence: index,
        }))

      // Prepare final values
      const finalValues: ProductFormData = {
        ...processedValues,
        // Set stock to 1 if it's a fighting chicken
        stock: processedValues.stock,
        mediaUrls: finalMediaUrls,
        description: processedValues.description ?? '',
      }

      // API call
      if (mode === 'edit' && product) {
        await axios.patch(endpoints.products.update(product.id), finalValues)
        toast.success('อัปเดตสินค้าสำเร็จ')
      } else {
        await axios.post(endpoints.products.create, finalValues)
        toast.success('เพิ่มสินค้าสำเร็จ')
      }

      router.push(paths.products)
      return
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <Form {...form}>
        <form
          id="product-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <ProductInformation form={form} categories={categories} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <ProductSettings form={form} />
              <ProductMediaUpload
                form={form}
                uploadedFiles={uploadedFiles}
                onFilesChange={handleFilesChange}
                onFileDelete={handleFileDelete}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 py-6 border-t">
            <Button
              type="submit"
              form="product-form"
              disabled={submitting.value || (!isDirty && mode === 'edit')}
            >
              {submitting.value
                ? 'กำลังดำเนินการ...'
                : mode === 'edit'
                  ? 'แก้ไข'
                  : 'สร้าง'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => router.push(paths.products)}
              disabled={submitting.value}
            >
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
